import { useQuery, useReactiveVar } from '@apollo/client';
import React, { useEffect, useMemo, useRef } from 'react';
import { FORM_PAGES } from '../../../components/Form/types';
import FormInfoFormQuestions from '../../../components/FormInfo/FormQuestions/FormInfo';
import { FORM_INFO_TYPES } from '../../../components/FormInfo/types';
import { Question } from '../../../components/Forms/FormQuestion/types';
import FormQuestionsCategory from '../../../components/Forms/FormQuestionsCategory';
import Animation from '../../../components/Shared/Animation/Animation';
import { CenteredLoadingSpinner } from '../../../components/Shared/LoadingSpinner/LoadingSpinner';
import useAnimatedPages from '../../../hooks/useAnimatedPages';
import { vars } from '../../../reactive';
import { setFormPage } from '../../../reactive/actions';
import { BranchForm } from '../../../types';
import { AnalyticsEvents, trackEvent } from '../../../utils/analytics';
import { GetForms } from '../../../utils/queries';

const FormBookingForms = ({ shouldNavigateToLastPage }: { shouldNavigateToLastPage?: boolean }) => {
  const isFormFillView = useReactiveVar(vars.isFormFillView);
  const selectedPets = useReactiveVar(vars.selectedPets);
  const selectedForms = useReactiveVar(vars.selectedForms);

  const { data: { getForms: requiredForms = [] } = {}, loading: loadingRequiredForms } = useQuery<{ getForms: BranchForm[] }>(GetForms, {
    variables: {
      id: selectedForms?.map(form => form.id)
    },
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    if (requiredForms.length) {
      vars.selectedForms(requiredForms);
    }
  }, [requiredForms]);

  const filterPetsByQuestionType = (question: BranchForm['Questions']['0']) => {
    if (question.updates_field === 'PetRecord_date_of_last_season') {
      return selectedPets.filter(pet => pet.gender === 'FEMALE');
    }
    return selectedPets;
  };

  const allQuestions: Question[] = useMemo(() => {
    return requiredForms
      ?.flatMap(form =>
        form.Questions.map(question => {
          const { defaultAnswers, ask_per_pet, AnswerOptions } = question;
          const hasAnswerOptions = !!AnswerOptions?.length;
          const newQuestion = requiredForms.length > 1 ? { ...question, title: `${form.name} - ${question.title}` } : question;
          if (!ask_per_pet) {
            const defaultAnswer = hasAnswerOptions ? defaultAnswers?.[0]?.AnswerOptionId : defaultAnswers?.[0]?.answer;
            return [{ formId: form.id, question: newQuestion, PetRecordId: null, defaultAnswer }];
          }

          return filterPetsByQuestionType(question).map(pet => {
            const petRecordId = pet?.PetRecord?.id!;
            const defaultValue = defaultAnswers?.find(answer => answer.PetRecordId === petRecordId);
            const defaultAnswer = hasAnswerOptions ? defaultValue?.AnswerOptionId : defaultValue?.answer;
            const newTitle = selectedPets.length > 1 ? `${newQuestion.title} (${pet?.name})` : newQuestion.title;
            return { formId: form.id, question: { ...newQuestion, title: newTitle }, PetRecordId: petRecordId, defaultAnswer };
          });
        })
      )
      .flat()
      .filter(question => question.formId);
  }, [requiredForms, selectedPets]);

  const questionsSplitByCategories = useMemo(() => {
    return allQuestions.reduce((acc, question) => {
      const existingCategory = acc.find(({ QuestionCategoryId }) => QuestionCategoryId === question.question.QuestionCategories?.[0]?.id);
      if (!existingCategory) {
        return [...acc, { QuestionCategoryId: question.question.QuestionCategories?.[0]?.id, questions: [question] }];
      }
      return acc.map(category => {
        if (category.QuestionCategoryId === question.question.QuestionCategories?.[0]?.id) {
          return { ...category, questions: [...category.questions, question] };
        }
        return category;
      });
    }, [] as { QuestionCategoryId: string; questions: Question[] }[]);
  }, [allQuestions]);

  useEffect(() => {
    if (!loadingRequiredForms) {
      if (allQuestions.length) {
        trackEvent(AnalyticsEvents.FORM_INITIATED);
      } else {
        setFormPage(isFormFillView ? FORM_PAGES.FORM_FILL_SUMMARY : FORM_PAGES.BOOKING_SUMMARY);
      }
    }
  }, [loadingRequiredForms, allQuestions]);

  return (
    <>
      {loadingRequiredForms && <CenteredLoadingSpinner />}
      {!loadingRequiredForms && !!questionsSplitByCategories?.length && (
        <FormBookingFormsContent questionsSplitByCategories={questionsSplitByCategories} shouldNavigateToLastPage={shouldNavigateToLastPage} allQuestions={allQuestions} />
      )}
    </>
  );
};

const FormBookingFormsContent = ({
  questionsSplitByCategories,
  shouldNavigateToLastPage,
  allQuestions
}: {
  questionsSplitByCategories: { QuestionCategoryId: string; questions: Question[] }[];
  shouldNavigateToLastPage?: boolean;
  allQuestions: Question[];
}) => {
  const formsAnswers = useReactiveVar(vars.formsAnswers);
  const isFormFillView = useReactiveVar(vars.isFormFillView);
  const { currentPage, decreaseCurrentPage, increaseCurrentPage } = useAnimatedPages(shouldNavigateToLastPage ? questionsSplitByCategories.length : isFormFillView ? 1 : 0);

  const defaultCategoryPageIndex = useRef<number>(shouldNavigateToLastPage ? questionsSplitByCategories[questionsSplitByCategories.length - 1]?.questions?.length - 1 : 0);

  const navigateToNextCategory = () => {
    defaultCategoryPageIndex.current = 0;
    if (currentPage === questionsSplitByCategories.length) {
      trackEvent(AnalyticsEvents.FORM_COMPLETED);
      setFormPage(isFormFillView ? FORM_PAGES.FORM_FILL_SUMMARY : FORM_PAGES.BOOKING_SUMMARY);
      return;
    }
    increaseCurrentPage();
  };

  const navigateToPreviousCategory = (categoryIndex: number) => {
    defaultCategoryPageIndex.current = 0;
    const previousCategory = questionsSplitByCategories[categoryIndex - 1];
    if (previousCategory) {
      defaultCategoryPageIndex.current = previousCategory.questions.length - 1;
    }

    if (currentPage === 1 && isFormFillView) {
      setFormPage(FORM_PAGES.BOOKING_SELECT_PETS);
      return;
    }

    decreaseCurrentPage();
  };

  return (
    <Animation animationKey={currentPage}>
      <>
        {currentPage === 0 && <FormInfoFormQuestions onSubmit={increaseCurrentPage} type={FORM_INFO_TYPES.FORM_QUESTIONS} previousPage={{ page: FORM_PAGES.BOOKING_SELECT_PETS }} />}
        {currentPage >= 1 && (
          <>
            {questionsSplitByCategories.map(
              (questionsSplitByCategory, index) =>
                currentPage === index + 1 && (
                  <FormQuestionsCategory
                    key={questionsSplitByCategory.QuestionCategoryId}
                    questionsSplitByCategory={questionsSplitByCategory}
                    formsAnswers={formsAnswers}
                    navigateToNextCategory={navigateToNextCategory}
                    allQuestions={allQuestions}
                    navigateToPreviousCategory={() => navigateToPreviousCategory(index)}
                    defaultCategoryPageIndex={defaultCategoryPageIndex.current}
                  />
                )
            )}
          </>
        )}
      </>
    </Animation>
  );
};

export default FormBookingForms;
